import { z } from "zod";

export const AlertSeverityList = [
  "success",
  "info",
  "warning",
  "error",
] as const;
export type AlertSeverity = (typeof AlertSeverityList)[number];

const ProcessTypeList = [
  "putExperience",
  "putExperienceError",
  "userError",
  "downloadExperience",
  "startExperience",
  "requestCogs",
  "invalidCogsCsvAlert",
  "duplicateExperience",
  "syncPricingFunction",
  "fileBuild",
] as const;

export type ProcessTypeType = (typeof ProcessTypeList)[number];

export type GetProcessIdOptions =
  | StartExperienceProcess
  | PutExperienceProcess
  | DownloadExperienceProcess
  | RequestCogsProcess
  | InvalidCogsProcess
  | DuplicateExperienceProcess
  | FileBuildProcess
  | SyncPricingFunctionProcess
  | PutExperienceError
  | UserError;

type BaseProcess = {
  type: ProcessTypeType;
};

interface PutExperienceProcess extends BaseProcess {
  type: "putExperience";
  experienceId: string;
}

interface PutExperienceError extends BaseProcess {
  type: "putExperienceError";
  experienceId: string;
}

interface UserError extends BaseProcess {
  type: "userError";
  orgId: string;
}

interface DownloadExperienceProcess extends BaseProcess {
  type: "downloadExperience";
  experienceId: string;
  datasetId: string;
}

interface StartExperienceProcess extends BaseProcess {
  type: "startExperience";
  experienceId: string;
}

interface RequestCogsProcess extends BaseProcess {
  type: "requestCogs";
  orgId: string;
}

interface InvalidCogsProcess extends BaseProcess {
  type: "invalidCogsCsvAlert";
  processId: string;
}

interface DuplicateExperienceProcess extends BaseProcess {
  type: "duplicateExperience";
  orgId: string;
  originalExperienceId: string;
}

interface SyncPricingFunctionProcess extends BaseProcess {
  type: "syncPricingFunction";
  orgId: string;
}

interface FileBuildProcess extends BaseProcess {
  type: "fileBuild";
  orgId: string;
}

export const ProcessStatusTableDtoSchema = z.object({
  id: z.string(),
  orgId: z.string(),
  type: z.enum(ProcessTypeList),
  severity: z.enum(AlertSeverityList),
  title: z.string(),
  isLoading: z.boolean().nullish(),
  loadingPercent: z.number().nullish(),
  message: z.string().nullish(),
  longMessage: z.string().nullish(),
  action: z
    .object({ href: z.string().nullish(), acknowledge: z.boolean().nullish() })
    .nullish(),
  actionMessage: z.string().nullish(),
});

export type ProcessStatusTableDto = z.infer<
  typeof ProcessStatusTableDtoSchema
> & {
  type: ProcessTypeType;
};

export type OrgAlertType = Omit<ProcessStatusTableDto, "orgId">;
